import { createSlice } from '@reduxjs/toolkit';
import { portalApi } from '../api/portalApi';

// Re-export RTK Query hook for direct component use
export { useGetSoldToShipToQuery } from '../api/portalApi';

// Helper to convert array of soldToShipTo records to the lookup-by-ProjectID shape
const buildLookup = (records) => {
  const lookup = {};
  if (Array.isArray(records)) {
    records.forEach((rec) => {
      if (rec.ProjectID) {
        lookup[rec.ProjectID] = {
          sellPrice: rec.InvoicePrice || 0,
          data: rec,
        };
      }
    });
  }
  return lookup;
};

/**
 * Backward-compatible thunk wrappers.
 */
export const fetchAllSellPrices = () => (dispatch) => {
  return dispatch(
    portalApi.endpoints.getSoldToShipTo.initiate(undefined, { forceRefetch: false })
  );
};

export const refreshSellPrices = () => (dispatch) => {
  return dispatch(
    portalApi.endpoints.getSoldToShipTo.initiate(undefined, { forceRefetch: true })
  );
};

const sellPriceSlice = createSlice({
  name: 'sellPrice',
  initialState: {
    data: {},
    loading: false,
    error: null,
    lastFetch: null
  },
  reducers: {
    clearSellPrices: (state) => {
      state.data = {};
      state.loading = false;
      state.error = null;
      state.lastFetch = null;
    },
    setSellPricesFromRecords: (state, action) => {
      state.data = buildLookup(action.payload || []);
      state.loading = false;
      state.error = null;
      state.lastFetch = new Date().toISOString();
    },
    setSellPriceForProject: (state, action) => {
      const { projectId, sellPrice, data } = action.payload;
      state.data[projectId] = { sellPrice, data };
    }
  },
  extraReducers: (builder) => {
    builder
      // ── RTK Query: getSoldToShipTo (all records – no projectID arg) ──────
      .addMatcher(
        (action) =>
          portalApi.endpoints.getSoldToShipTo.matchPending(action) &&
          action.meta?.arg?.originalArgs === undefined,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) =>
          portalApi.endpoints.getSoldToShipTo.matchFulfilled(action) &&
          action.meta?.arg?.originalArgs === undefined,
        (state, action) => {
          state.loading = false;
          state.data = buildLookup(action.payload);
          state.lastFetch = new Date().toISOString();
          state.error = null;
        }
      )
      .addMatcher(
        (action) =>
          portalApi.endpoints.getSoldToShipTo.matchRejected(action) &&
          action.meta?.arg?.originalArgs === undefined,
        (state, action) => {
          state.loading = false;
          state.error = action.error?.message ?? 'Failed to fetch sell prices';
        }
      );
  }
});

export const { clearSellPrices, setSellPricesFromRecords, setSellPriceForProject } = sellPriceSlice.actions;

// Selectors
export const selectSellPriceData = (state) => state.sellPrice.data;
export const selectSellPriceLoading = (state) => state.sellPrice.loading;
export const selectSellPriceError = (state) => state.sellPrice.error;
export const selectSellPriceForProject = (projectId) => (state) =>
  state.sellPrice.data[projectId]?.sellPrice || null;
export const selectSoldToShipToDataForProject = (projectId) => (state) =>
  state.sellPrice.data[projectId]?.data || null;

export default sellPriceSlice.reducer;
